import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ADANA_DISTRICTS } from "@/data/adana";
import founderImage from "@/assets/gurkan-sonmez.jpg";

export const SITE_URL = "https://www.cornergayrimenkul.com.tr";

export interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  schemaJson?: object | object[];
  noindex?: boolean;
}

const defaultDescriptions: Record<string, string> = {
  tr: "Adana genelinde satılık ve kiralık daire, villa, arsa ve işyeri ilanları. Corner Gayrimenkul ile ilçe rehberlerini inceleyin ve gayrimenkul danışmanlığı alın.",
  en: "Explore apartments, villas, land and commercial properties for sale and rent in Adana with Corner Gayrimenkul. District guides and property consulting.",
};

export function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage = founderImage,
  ogType = "website",
  schemaJson,
  noindex = false,
}: SEOHeadProps) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const language = (i18n.resolvedLanguage || i18n.language || "tr").split("-")[0];
  const currentLang = language === "en" ? "en" : "tr";
  const pageTitle = title
    ? /corner gayrimenkul/i.test(title) ? title : `${title} | Corner Gayrimenkul`
    : "Adana Satılık ve Kiralık Emlak | Corner Gayrimenkul";
  const pageDescription = description || defaultDescriptions[currentLang];
  const path = (canonicalPath ?? pathname).split(/[?#]/)[0];
  const normalizedPath = `/${path.replace(/^\/+|\/+$/g, "")}`;
  const canonicalUrl = `${SITE_URL}${normalizedPath}`;
  const imageUrl = /^https?:\/\//.test(ogImage) ? ogImage : `${SITE_URL}/${ogImage.replace(/^\//, "")}`;
  const excludeIndex = noindex || /^\/(dashboard|admin)(\/|$)/.test(pathname);
  const organizationId = `${SITE_URL}/#organization`;
  const websiteId = `${SITE_URL}/#website`;

  const organization = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "@id": organizationId,
    name: "Corner Gayrimenkul",
    url: `${SITE_URL}/`,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Gürselpaşa Mah. Aliya İzzetbegoviç Bulv.",
      addressLocality: "Seyhan",
      addressRegion: "Adana",
      postalCode: "01200",
      addressCountry: "TR",
    },
    areaServed: ADANA_DISTRICTS.map((district) => ({
      "@type": "AdministrativeArea",
      name: `${district.name}, Adana`,
    })),
  };
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": websiteId,
    name: "Corner Gayrimenkul",
    url: `${SITE_URL}/`,
    inLanguage: currentLang,
    publisher: { "@id": organizationId },
  };
  const webpage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    description: pageDescription,
    inLanguage: currentLang,
    isPartOf: { "@id": websiteId },
    publisher: { "@id": organizationId },
  };
  const additionalSchemas = schemaJson ? Array.isArray(schemaJson) ? schemaJson : [schemaJson] : [];
  const schemas = [organization, ...(normalizedPath === "/" ? [website] : []), webpage, ...additionalSchemas];

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={excludeIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <meta name="googlebot" content={excludeIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      {!excludeIndex && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:locale" content={currentLang === "en" ? "en_GB" : "tr_TR"} />
      <meta property="og:site_name" content="Corner Gayrimenkul" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={imageUrl} />
      {!excludeIndex && (
        <script type="application/ld+json">
          {JSON.stringify(schemas).replace(/</g, "\\u003c")}
        </script>
      )}
    </Helmet>
  );
}
